/**
 * Admin / seller product search (table + status filters).
 * Buyer marketplace category context (breadcrumbs, market stats, expandable copy)
 * lives in `Pages/Common/ProductSearch.tsx` when `?categoryPath=` is used.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, RefreshCw, CheckCircle, XCircle, Info } from "lucide-react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import {
  getProductDetailPath,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  AdminproductTableConfig,
  TanStackDataTable,
} from "@b2b/ui-components";
import { useAuth } from "../../auth/Authprovider";
import { useProductList } from "../../hooks/useProductList";
import { useProductActions } from "../../hooks/useProductActions";
import { AdminProduct } from "../../types/product.types";
import {
  UpdatedStatusFilter,
} from "../../hooks/useProductList";
import { createProductColumns } from "./ProductList.columns";
import { RejectProductReasonDialog } from "./RejectProductReasonDialog";

interface ProductSearchProps {
  role?: "admin" | "seller";
}

const ProductSearch = ({ role: roleProp }: ProductSearchProps) => {
  const { userRole, isLoading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Ensure role is only "admin" or "seller" (not "buyer")
  const role: "admin" | "seller" =
    roleProp || (userRole === "seller" ? "seller" : "admin");
  const isAdmin = role === "admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [updatedStatusFilter, setUpdatedStatusFilter] =
    useState<UpdatedStatusFilter>("all");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [initialListLoad, setInitialListLoad] = useState(true);
  const [listingRejectProductId, setListingRejectProductId] = useState<
    string | null
  >(null);
  const [listingRejectReason, setListingRejectReason] = useState("");

  const showToast = useCallback(
    (type: "success" | "error" | "info", message: string) => {
      setToast({ type, message });
      setTimeout(() => setToast(null), 5000);
    },
    []
  );

  const closeListingRejectDialog = useCallback(() => {
    setListingRejectProductId(null);
    setListingRejectReason("");
  }, []);

  const openListingRejectDialog = useCallback((productId: string) => {
    setListingRejectProductId(productId);
    setListingRejectReason("");
  }, []);

  // Map status filter to activeTab format
  const getActiveTab = () => {
    if (statusFilter === "all") return "all";
    return statusFilter;
  };

  const { products, loading, error, pagination, setPagination, refresh } =
    useProductList({
      role,
      searchTerm,
      activeTab: getActiveTab(),
      updatedStatusFilter,
      autoFetch: true,
    });

  const {
    loading: actionsLoading,
    approveProduct,
    rejectProduct,
    submitForApproval,
    deleteProduct,
  } = useProductActions({
    role,
    onSuccess: () => refresh(),
    onError: (message) => showToast("error", message),
  });

  // Build filters
  useEffect(() => {
    const filters: string[] = [];
    const statusLabelMap: Record<string, string> = {
      all: "All Status",
      pending: "Pending",
      "pending-updates": "Pending updates",
      "updated-status": "Latest status",
      approved: "Approved",
      rejected: "Rejected",
      draft: "Draft",
    };
    const updatedStatusLabelMap: Record<UpdatedStatusFilter, string> = {
      all: "All latest status",
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
    };
    if (searchTerm) filters.push(`Search: "${searchTerm}"`);
    if (categoryFilter !== "all") filters.push(`Category: ${categoryFilter}`);
    if (statusFilter !== "all") {
      filters.push(`Status: ${statusLabelMap[statusFilter] || statusFilter}`);
    }
    if (statusFilter === "updated-status" && updatedStatusFilter !== "all") {
      filters.push(
        `Latest status: ${updatedStatusLabelMap[updatedStatusFilter]}`
      );
    }
    setActiveFilters(filters);
  }, [searchTerm, categoryFilter, statusFilter, updatedStatusFilter]);

  // Reset pagination when search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchTerm, statusFilter, categoryFilter, updatedStatusFilter, setPagination]);

  useEffect(() => {
    if (statusFilter !== "updated-status" && updatedStatusFilter !== "all") {
      setUpdatedStatusFilter("all");
    }
  }, [statusFilter, updatedStatusFilter]);

  useEffect(() => {
    if (!authLoading) {
      setInitialListLoad(false);
    }
  }, [authLoading]);

  // Perform search
  const handleSearch = async () => {
    await refresh();
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setStatusFilter("all");
    setUpdatedStatusFilter("all");
  };

  const handleRemoveFilter = (filter: string) => {
    if (filter.startsWith("Search:")) {
      setSearchTerm("");
    } else if (filter.startsWith("Category:")) {
      setCategoryFilter("all");
    } else if (filter.startsWith("Status:")) {
      setStatusFilter("all");
    } else if (filter.startsWith("Latest status:")) {
      setUpdatedStatusFilter("all");
    }
  };

  const handleViewDetails = (productId: string) => {
    navigate(getProductDetailPath(productId));
  };

  const handleEditProduct = (productId: string) => {
    if (isAdmin) {
      navigate(`/admin/create_product/${productId}`);
    } else {
      navigate(`/seller/create_product/${productId}`);
    }
  };

  const handleApproveProduct = async (productId: string) => {
    const success = await approveProduct(productId);
    if (success) {
      showToast("success", "Product approved successfully");
    } else {
      showToast("error", "Failed to approve product");
    }
  };

  const confirmListingReject = useCallback(async () => {
    if (!listingRejectProductId) return;
    const trimmed = listingRejectReason.trim();
    if (!trimmed) {
      showToast(
        "error",
        "Please enter a rejection reason. Sellers will see this on their product list."
      );
      return;
    }
    const success = await rejectProduct(listingRejectProductId, trimmed);
    if (success) {
      showToast("success", "Product rejected successfully");
      closeListingRejectDialog();
    }
  }, [
    listingRejectProductId,
    listingRejectReason,
    rejectProduct,
    showToast,
    closeListingRejectDialog,
  ]);

  const handleSubmitForApproval = async (productId: string) => {
    const success = await submitForApproval(productId);
    if (success) {
      showToast("success", "Product submitted for approval successfully");
    } else {
      showToast("error", "Failed to submit product");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    const success = await deleteProduct(productId);
    if (success) {
      showToast("success", "Product deleted successfully");
    } else {
      showToast("error", "Failed to delete product");
    }
  };

  const handleDuplicateProduct = async (productId: string) => {
    try {
      const response = isAdmin
        ? await adminService.duplicateProduct(productId)
        : await sellerService.duplicateProduct(productId);

      if (response?.success) {
        refresh();
        showToast("success", "Product duplicated successfully");
      } else {
        showToast("error", response?.message || "Failed to duplicate product");
      }
    } catch {
      showToast("error", "Failed to duplicate product");
    }
  };

  // Create column definitions with memoization
  const columns = useMemo(
    () =>
      createProductColumns({
        isAdmin,
        loading,
        actionsLoading,
        onViewDetails: handleViewDetails,
        onEditProduct: handleEditProduct,
        onApproveProduct: handleApproveProduct,
        onRejectProduct: openListingRejectDialog,
        onSubmitForApproval: handleSubmitForApproval,
        onDeleteProduct: handleDeleteProduct,
        onDuplicateProduct: handleDuplicateProduct,
        statusStyles: AdminproductTableConfig.styles?.statusStyles,
      }),
    [
      isAdmin,
      loading,
      actionsLoading,
      handleViewDetails,
      handleEditProduct,
      handleApproveProduct,
      openListingRejectDialog,
      handleSubmitForApproval,
      handleDeleteProduct,
      handleDuplicateProduct,
    ]
  );

  return (
    <div className="w-full max-w-none mx-auto px-2 sm:px-3 lg:px-4 pb-8 pt-4 sm:pt-6 space-y-6">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-blue-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : toast.type === "error" ? (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {isAdmin && (
        <RejectProductReasonDialog
          open={listingRejectProductId !== null}
          title="Reject product"
          description="Sellers will see this reason on their product list so they know what to fix before resubmitting."
          reason={listingRejectReason}
          onReasonChange={setListingRejectReason}
          onClose={closeListingRejectDialog}
          onConfirm={() => void confirmListingReject()}
          loading={actionsLoading}
        />
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            {isAdmin ? "Search Products" : "Search My Products"}
          </h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "Search and filter products by name, SKU, category, or seller"
              : "Search and filter your products by name, SKU, or category"}
          </p>
        </div>
        {/* <Button
          variant="outline"
          onClick={handleClearSearch}
          disabled={
            !searchTerm &&
            categoryFilter === "all" &&
            statusFilter === "all" &&
            updatedStatusFilter === "all"
          }
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Clear Filters
        </Button> */}
      </div>

      {/* Search Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder={
                  isAdmin
                    ? "Search products by name, SKU, category, or seller..."
                    : "Search products by name, SKU, or category..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 border-orange-500">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem
                  value="all"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  All Status
                </SelectItem>
                <SelectItem
                  value="pending"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Pending
                </SelectItem>
                {/* {isAdmin && (
                  <SelectItem value="pending-updates">Latest Pending</SelectItem>
                )} */}
                {isAdmin && (
                  <SelectItem
                    value="updated-status"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    Latest status
                  </SelectItem>
                )}
                <SelectItem
                  value="approved"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Approved
                </SelectItem>
                <SelectItem
                  value="rejected"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Rejected
                </SelectItem>
                {!isAdmin && (
                  <SelectItem
                    value="draft"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    Draft
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {isAdmin && statusFilter === "updated-status" && (
              <Select
                value={updatedStatusFilter}
                onValueChange={(value: UpdatedStatusFilter) =>
                  setUpdatedStatusFilter(value)
                }
              >
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Latest status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem
                    value="all"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    All latest status
                  </SelectItem>
                  <SelectItem
                    value="pending"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    Pending
                  </SelectItem>
                  <SelectItem
                    value="approved"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    Approved
                  </SelectItem>
                  <SelectItem
                    value="rejected"
                    className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                  >
                    Rejected
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button onClick={handleSearch} variant="outline" disabled={loading}>
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearSearch}>
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-muted-foreground">
                Active filters:
              </span>
              {activeFilters.map((filter, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => handleRemoveFilter(filter)}
                >
                  {filter}
                  <X className="h-3 w-3" />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Search Results{" "}
            {products.length > 0 && `(${pagination.total} found)`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading || initialListLoad ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              <span>Searching products...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchTerm ||
                categoryFilter !== "all" ||
                statusFilter !== "all" ||
                updatedStatusFilter !== "all"
                  ? "No products found matching your search criteria."
                  : "No products found."}
              </p>
            </div>
          ) : (
            <TanStackDataTable
              data={products}
              columns={columns}
              isLoading={loading}
              serverPagination={
                pagination.totalPages > 1 && pagination.total > pagination.limit
                  ? {
                      pageIndex: pagination.page,
                      pageSize: pagination.limit,
                      total: pagination.total,
                      totalPages: pagination.totalPages,
                      onPageChange: (page: number) => {
                        setPagination((prev) => ({ ...prev, page }));
                      },
                    }
                  : undefined
              }
              emptyMessage={
                searchTerm ||
                categoryFilter !== "all" ||
                statusFilter !== "all" ||
                updatedStatusFilter !== "all"
                  ? "No products found matching your search criteria."
                  : "No products found."
              }
              styles={{
                headerBg: AdminproductTableConfig.styles?.headerBg,
                headerText: AdminproductTableConfig.styles?.headerText,
                rowHover: AdminproductTableConfig.styles?.rowHover,
              }}
              config={{
                enableSorting: true,
                enablePagination: false, // Server-side pagination
                enableZebraStriping: false,
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductSearch;
